import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, Flex, Box, TextArea, Text, Select } from "@radix-ui/themes";
import {
  useImagesControllerStartGenerateImage,
  useImagesControllerGetGenerateImageStatus,
  useImagesControllerSetTrackArtworkFromGenerated,
  useImagesControllerSetAlbumArtworkFromGenerated,
  useImagesControllerSetProfilePictureFromGenerated,
} from "@/lib/lutely-api/gen/images/images";
import type { ImageGenerationStatusDtoStatus } from "@/lib/lutely-api/gen/dtos";
import type { ImageApplyTarget } from "../types";
import {
  invalidateTrackQueries,
  invalidateAlbumQueries,
  invalidateCreatorQueries,
} from "@/lib/lutely-api/api-utils";
import { useQueryClient } from "@tanstack/react-query";
import { useFeatureGate } from "@statsig/react-bindings";
import {
  ArrowRight2Icon,
  AddImageIcon,
  DownloadIcon,
  FullscreenIcon,
  ShuffleIcon,
} from "@/assets/icons";
import { IconButton } from "@/components/ui/IconButton";
import {
  buildRandomStyledDraftPrompt,
  composeStyledPrompt,
  DEFAULT_STYLE_CATEGORY,
  getStyleLabel,
  STYLE_CATEGORY_GROUPS,
  type ImageStyleCategory,
} from "../prompts";
import { toast } from "react-toastify";

type GenerativeImagePanelProps = {
  creatorId: string;
  target: ImageApplyTarget;
  onApplied: () => void;
  onUnsavedChange?: (hasUnsaved: boolean) => void;
};

type GenerationItem = {
  id: string;
  prompt: string;
  userPrompt?: string;
  status: ImageGenerationStatusDtoStatus | null;
  previewUrl?: string;
  bucket?: string;
  fileKey?: string;
  isRandom?: boolean;
  styleCategory?: ImageStyleCategory;
  progress?: number;
  targetProgress?: number;
  startedAt: number;
};

type StyleSelectorProps = {
  value: ImageStyleCategory;
  onValueChange: (value: ImageStyleCategory) => void;
  onOpenChange: (open: boolean) => void;
};

const StyleSelector = memo(function StyleSelector({
  value,
  onValueChange,
  onOpenChange,
}: StyleSelectorProps) {
  return (
    <Select.Root
      value={value}
      onOpenChange={onOpenChange}
      onValueChange={(nextValue) =>
        onValueChange(nextValue as ImageStyleCategory)
      }
    >
      <Select.Trigger className="w-[160px] sm:w-[200px] h-[28px] text-[#525966]" />
      <Select.Content position="popper">
        {STYLE_CATEGORY_GROUPS.map((group, groupIndex) => (
          <Fragment key={group.groupLabel}>
            {groupIndex > 0 && <Select.Separator />}
            <Select.Group>
              <Select.Label>{group.groupLabel}</Select.Label>
              {group.options.map((option) => (
                <Select.Item key={option.value} value={option.value}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Group>
          </Fragment>
        ))}
      </Select.Content>
    </Select.Root>
  );
});

function ElapsedTimer({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    const timer = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [startedAt]);
  return <Text className="text-[11px] text-[#525966]">{elapsed}s</Text>;
}

function statusToFallbackProgress(
  status: ImageGenerationStatusDtoStatus | null | undefined,
) {
  switch (status) {
    case "IN_QUEUE":
      return 5;
    case "IN_PROGRESS":
      return 30;
    case "COMPLETED":
      return 100;
    default:
      return 0;
  }
}

function tickGenerationProgressItem(
  generation: GenerationItem,
): GenerationItem {
  const current = generation.progress ?? 0;

  if (generation.status === "COMPLETED" || generation.status === "FAILED") {
    return current === 100 ? generation : { ...generation, progress: 100 };
  }

  // Smooth toward the target progress (set from real handler progress or status fallback)
  const target = generation.targetProgress ?? current;
  if (current >= target) return generation;
  const step = Math.max(0.5, (target - current) * 0.15);
  return { ...generation, progress: Math.min(current + step, target) };
}

function tickGenerationProgressList(prev: GenerationItem[]): GenerationItem[] {
  if (prev.length === 0) return prev;

  let didChange = false;
  const next = prev.map((generation) => {
    const updated = tickGenerationProgressItem(generation);
    if (updated !== generation) {
      didChange = true;
    }
    return updated;
  });

  return didChange ? next : prev;
}

export default function GenerativeImagePanel({
  creatorId,
  target,
  onApplied,
  onUnsavedChange,
}: GenerativeImagePanelProps) {
  const queryClient = useQueryClient();
  const [prompt, setPrompt] = useState("");
  const [selectedStyleCategory, setSelectedStyleCategory] =
    useState<ImageStyleCategory>(DEFAULT_STYLE_CATEGORY);
  const [isStyleMenuOpen, setIsStyleMenuOpen] = useState(false);
  const [generations, setGenerations] = useState<GenerationItem[]>([]);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [fullscreenUrl, setFullscreenUrl] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement | null>(null);
  const isGeneratingRef = useRef(false);
  const hasInProgressGeneration = generations.some(
    (g) => g.status === "IN_QUEUE" || g.status === "IN_PROGRESS",
  );
  const hasCompleted = generations.some((g) => g.status === "COMPLETED");
  // Image generation runs on the GCP media-gpu service — gate it off while
  // generation is disabled for the migration.
  const disableAudiolabAssetGeneration = useFeatureGate(
    "disable_audiolab_asset_generation",
  ).value;
  const disableGenerate =
    isGeneratingRef.current ||
    hasInProgressGeneration ||
    disableAudiolabAssetGeneration;
  const hasUnsaved = hasInProgressGeneration || hasCompleted;

  const setTrackGenerated = useImagesControllerSetTrackArtworkFromGenerated();
  const setAlbumGenerated = useImagesControllerSetAlbumArtworkFromGenerated();
  const setProfileGenerated =
    useImagesControllerSetProfilePictureFromGenerated();
  const startGenerate = useImagesControllerStartGenerateImage();
  const statusQuery = useImagesControllerGetGenerateImageStatus(
    activeJobId ?? "",
    {
      query: {
        enabled: !!activeJobId,
        refetchInterval: (query) => {
          if (isStyleMenuOpen) {
            return false;
          }

          const status = query.state.data?.status;

          if (status === "COMPLETED" || status === "FAILED") {
            return false;
          }

          return 2000;
        },
      },
    },
  );
  const handleStyleValueChange = useCallback((value: ImageStyleCategory) => {
    setSelectedStyleCategory(value);
  }, []);

  async function handleGenerate(
    promptText: string,
    isRandom = false,
    styleCategory?: ImageStyleCategory,
    userPrompt?: string,
  ) {
    if (isGeneratingRef.current) return;

    try {
      isGeneratingRef.current = true;

      const res = await startGenerate.mutateAsync({
        data: { creatorId, prompt: promptText },
      });

      setGenerations((prev) => [
        ...prev,
        {
          id: res.jobId,
          prompt: promptText,
          userPrompt,
          status: "IN_QUEUE",
          isRandom,
          styleCategory,
          progress: 5,
          startedAt: Date.now(),
        },
      ]);

      setActiveJobId(res.jobId);
    } finally {
      isGeneratingRef.current = false;
    }
  }

  async function generateWithUserPrompt() {
    if (!prompt.trim() || disableGenerate) return;

    const userInput = prompt.trim();
    const promptText = composeStyledPrompt({
      userPrompt: prompt,
      styleCategory: selectedStyleCategory,
    });
    setPrompt("");

    await handleGenerate(promptText, false, selectedStyleCategory, userInput);
  }

  async function generateWithRandomPrompt() {
    if (disableGenerate) return;

    const randomPrompt = buildRandomStyledDraftPrompt(selectedStyleCategory);
    await handleGenerate(randomPrompt, true, selectedStyleCategory);
  }

  async function handleApply(gen: GenerationItem) {
    if (gen.status !== "COMPLETED") return;

    try {
      if (target.type === "track") {
        await setTrackGenerated.mutateAsync({
          trackId: target.trackId,
          data: { creatorId, bucket: gen.bucket!, fileKey: gen.fileKey! },
        });
        await invalidateTrackQueries(queryClient, target.trackId);
      }

      if (target.type === "album") {
        await setAlbumGenerated.mutateAsync({
          albumId: target.albumId,
          data: { creatorId, bucket: gen.bucket!, fileKey: gen.fileKey! },
        });
        await invalidateAlbumQueries(queryClient, target.albumId);
      }

      if (target.type === "profile") {
        await setProfileGenerated.mutateAsync({
          creatorId: target.creatorId,
          data: { bucket: gen.bucket!, fileKey: gen.fileKey! },
        });
        await invalidateCreatorQueries(queryClient);
      }

      toast.success("Artwork applied successfully");
      onApplied();
    } catch {
      toast.error("Failed to apply artwork. Please try again.");
    }
  }

  // Update generation status when polling returns new data
  useEffect(() => {
    if (!statusQuery.data || !activeJobId || isStyleMenuOpen) return;

    setGenerations((prev) =>
      prev.map((g) => {
        if (g.id !== activeJobId) return g;

        const newStatus = statusQuery.data.status;
        const realProgress = statusQuery.data.progress;
        const target =
          realProgress != null
            ? realProgress
            : statusToFallbackProgress(newStatus);

        return {
          ...g,
          status: newStatus,
          previewUrl: statusQuery.data.previewUrl,
          bucket: statusQuery.data.bucket,
          fileKey: statusQuery.data.fileKey,
          targetProgress: target,
        };
      }),
    );
  }, [statusQuery.data, activeJobId, isStyleMenuOpen]);

  // Scroll to bottom of results when a new generation is added
  useEffect(() => {
    if (!resultsRef.current) return;

    resultsRef.current.scrollTo({
      top: resultsRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [generations.length]);

  // Prevent background scrolling when fullscreen preview is open
  useEffect(() => {
    document.body.style.overflow = fullscreenUrl ? "hidden" : "";
  }, [fullscreenUrl]);

  useEffect(() => {
    onUnsavedChange?.(hasUnsaved);
  }, [hasUnsaved, onUnsavedChange]);

  // Smooth progression between real progress updates
  useEffect(() => {
    if (!hasInProgressGeneration || isStyleMenuOpen) return;

    const interval = setInterval(() => {
      setGenerations(tickGenerationProgressList);
    }, 500);

    return () => clearInterval(interval);
  }, [hasInProgressGeneration, isStyleMenuOpen]);

  return (
    <Flex
      direction="column"
      gap="2"
      className="w-full h-[456px] overflow-hidden"
    >
      {/* Results */}
      <Box
        ref={resultsRef}
        px={{ initial: "2", sm: "6" }}
        className="flex-1 min-h-0 overflow-y-auto scrollbar-thin w-full rounded-md border border-[#ECEFF4] bg-white py-3"
      >
        {generations.length > 0 ? (
          <Flex direction="column" gap="6">
            {generations.map((gen) => {
              let randomLabel = "Random image";
              if (
                gen.styleCategory &&
                gen.styleCategory !== DEFAULT_STYLE_CATEGORY
              ) {
                randomLabel = `Styled draft (${getStyleLabel(gen.styleCategory)})`;
              }

              return (
                <Flex key={gen.id} direction="column" gap="3">
                  {/* Prompt */}
                  <Flex
                    direction="row"
                    align="center"
                    gap={{ initial: "2", sm: "4" }}
                  >
                    <ArrowRight2Icon className="h-6 w-6 sm:h-7 sm:w-7 shrink-0 bg-gray-200 text-[#878E9A] rounded-full p-1" />
                    <Text className="text-[14px] text-[#525966]">
                      {gen.isRandom
                        ? randomLabel
                        : (gen.userPrompt ?? gen.prompt)}
                    </Text>
                  </Flex>

                  <Flex
                    direction={{ initial: "column", sm: "row" }}
                    align={{ initial: "start", sm: "center" }}
                    gap="4"
                  >
                    {/* Image */}
                    <Box ml={{ initial: "30px", sm: "45px" }}>
                      <Flex
                        align="center"
                        justify="center"
                        className="relative h-[240px] w-[240px] bg-gray-200 rounded-md"
                      >
                        {gen.previewUrl ? (
                          <>
                            <img
                              src={gen.previewUrl}
                              alt="Generated preview"
                              className="max-h-full max-w-full object-contain"
                            />

                            <IconButton
                              onClick={() => setFullscreenUrl(gen.previewUrl!)}
                              className="absolute top-3 right-3 rounded-full bg-white/70 p-1.5 hover:bg-white/90"
                              aria-label="View fullscreen"
                            >
                              <FullscreenIcon className="h-5 w-5 fill-blue-900" />
                            </IconButton>
                          </>
                        ) : (
                          <AddImageIcon className="h-[120px] w-[120px] text-foreground-quinary" />
                        )}
                      </Flex>
                    </Box>

                    {/* Actions / Progress */}
                    {gen.status === "COMPLETED" && gen.previewUrl ? (
                      <Flex
                        direction={{ initial: "row", sm: "column" }}
                        gap={{ initial: "4", sm: "3" }}
                        mt={{ initial: "1", sm: "0" }}
                        ml={{ initial: "20px", sm: "0" }}
                        align="center"
                      >
                        <Button
                          onClick={() => handleApply(gen)}
                          radius="full"
                          className="text-white text-[14px] font-semibold"
                        >
                          <AddImageIcon className="h-5 w-5" />
                          {target.type === "profile"
                            ? "Set as Avatar"
                            : `Add to ${target.type === "track" ? "Track" : "Album"}`}
                        </Button>

                        <Button
                          variant="ghost"
                          onClick={() => window.open(gen.previewUrl!, "_blank")}
                          className="text-blue-900 text-[14px] font-semibold"
                        >
                          <DownloadIcon className="h-5 w-5" />
                          Download
                        </Button>
                      </Flex>
                    ) : (
                      <Box
                        ml={{ initial: "35px", sm: "0" }}
                        className="w-[220px]"
                      >
                        <Text className="text-[14px] text-purple-7 font-semibold">
                          {gen.status === "IN_QUEUE"
                            ? "In queue..."
                            : "Creating..."}
                        </Text>
                        <ElapsedTimer startedAt={gen.startedAt} />
                        <Box className="h-1.5 mt-2 w-full bg-gray-200 rounded-full overflow-hidden">
                          <Box className="h-full w-1/3 bg-purple-7 rounded-full animate-[indeterminate_1.5s_ease-in-out_infinite]" />
                        </Box>
                      </Box>
                    )}
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        ) : (
          <Flex
            align="center"
            justify="center"
            className="h-full min-h-[180px]"
          >
            <Text className="text-[#525966] text-[14px]">
              Images will appear here
            </Text>
          </Flex>
        )}
      </Box>

      {/* New Prompt */}
      <Flex direction="column" gap="1" className="shrink-0">
        <Box className="w-full bg-white">
          <TextArea
            placeholder="Describe your image"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={1}
            maxLength={300}
            style={{
              boxShadow: "none",
            }}
            className="border-none ring-0 outline-none"
          />
          <Flex justify="start" align="center" className="px-3">
            <StyleSelector
              value={selectedStyleCategory}
              onValueChange={handleStyleValueChange}
              onOpenChange={setIsStyleMenuOpen}
            />
          </Flex>
          <Flex justify="between" align="center" className="p-2">
            <Button
              onClick={generateWithRandomPrompt}
              disabled={disableGenerate}
              radius="full"
              className="text-blue-900 text-[12px] h-[28px] font-semibold bg-background-neutral-quarternary px-2 disabled:opacity-40 hover:text-white hover:bg-black"
              aria-label="Create random image"
            >
              <Text className="flex items-center gap-1">
                <ShuffleIcon className="h-5 w-5 fill-current" />
                Create Random
              </Text>
            </Button>

            <IconButton
              onClick={generateWithUserPrompt}
              disabled={disableGenerate || !prompt.trim()}
              className="disabled:opacity-40 bg-purple-7 text-white rounded-full hover:bg-purple-8"
              aria-label="Create image"
            >
              <ArrowRight2Icon className="h-7 w-7 p-1" />
            </IconButton>
          </Flex>
        </Box>

        {startGenerate.error && (
          <Text color="red" size="2">
            Failed to create image
          </Text>
        )}
      </Flex>

      {fullscreenUrl && (
        <Flex
          position="fixed"
          inset="0"
          align="center"
          justify="center"
          className="z-[9999] bg-black/90"
          role="dialog"
          aria-modal="true"
          aria-label="Fullscreen image preview"
          onClick={() => setFullscreenUrl(null)}
        >
          <img
            src={fullscreenUrl}
            alt="Fullscreen preview"
            className="max-h-full max-w-full object-contain"
          />
        </Flex>
      )}
    </Flex>
  );
}
